import apiClient from '../../../services/apiClient';

// ---- Lesson + slide shapes (GET /api/v1/lessons/:id) ------------------------
// The backend owns the canonical shape; we keep these permissive but typed for
// the fields the player actually reads.

// How the player decides the "next" slide may be unlocked / auto-advanced.
//  - 'manual'      : learner clicks next at will
//  - 'on_audio_end': auto-advance + gate next until the slide's audio finished
//  - 'on_widget'   : gate next until an embedded widget reports complete
export type AdvanceRule = 'manual' | 'on_audio_end' | 'on_widget' | string;

// A reference to an interactive widget embedded in a slide. The lab task owns
// the renderer; we only pass the ref through to <WidgetRenderer>. Kept loose.
export interface WidgetRef {
  id?: string;
  type?: string;
  // Arbitrary widget configuration — forwarded verbatim to the renderer.
  [key: string]: unknown;
}

export interface Slide {
  id: string;
  index: number;
  heading?: string | null;
  markdown_body?: string | null;
  speaker_script?: string | null;
  widget_refs?: WidgetRef[] | null;
  advance_rule?: AdvanceRule | null;
}

export interface LessonMeta {
  id: string;
  title: string;
  // Optional surrounding context the player surfaces when present.
  module_id?: string;
  week_id?: string;
  course_id?: string;
  [key: string]: unknown;
}

export interface LessonResponse {
  lesson: LessonMeta;
  slides: Slide[];
}

// One entry of the pre-synthesized audio manifest (GET /lessons/:id/audio).
export interface AudioManifestItem {
  slide_id: string;
  r2_key?: string;
  public_url?: string | null;
  duration_ms?: number | null;
}

export interface AudioManifest {
  slides: AudioManifestItem[];
}

// ---- Progress shapes (GET/PUT /api/v1/progress) -----------------------------
export type ProgressStatus = 'not_started' | 'in_progress' | 'completed';

export interface LessonProgress {
  lesson_id: string;
  slide_index: number;
  audio_position_ms: number;
  status: ProgressStatus;
}

// Full lesson: meta + ordered slides. Slides are sorted defensively by index.
export async function getLesson(lessonId: string): Promise<LessonResponse> {
  const { data } = await apiClient.get<LessonResponse>(`/api/v1/lessons/${lessonId}`);
  const slides = [...(data.slides ?? [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  return { ...data, slides };
}

// Pre-synthesized R2 audio manifest for the lesson. May be empty if a lesson's
// narration has not been generated yet — callers must handle the empty case.
export async function getLessonAudio(lessonId: string): Promise<AudioManifest> {
  const { data } = await apiClient.get<AudioManifest>(`/api/v1/lessons/${lessonId}/audio`);
  return { slides: data.slides ?? [] };
}

// Resume point for the current user on a lesson. Returns null when the backend
// has no stored progress (treated as a fresh start).
//
// The route replies { progress: {...} | null } (routes/progress.js `shape()`),
// not the progress fields at the top level — this was unwrapping the wrong
// object (returning the whole `{ progress }` envelope as if it WERE the
// progress row), so `.slide_index` / `.audio_position_ms` / `.status` were
// always undefined and resume-from-where-you-left-off silently never worked.
export async function getProgress(lessonId: string): Promise<LessonProgress | null> {
  try {
    const { data } = await apiClient.get<{ progress: LessonProgress | null }>('/api/v1/progress', {
      params: { lesson_id: lessonId },
    });
    return data?.progress ?? null;
  } catch {
    return null;
  }
}

// Persist the resume point. Fire-and-forget at the call sites (throttled).
export async function putProgress(progress: LessonProgress): Promise<void> {
  await apiClient.put('/api/v1/progress', progress);
}

export interface CompleteLessonResponse {
  progress: LessonProgress;
  awarded_xp: number;
}

// Mark the lesson complete (awards XP server-side, flips status to completed;
// idempotent — a revisit awards 0). Returns awarded_xp so the caller can
// update a running XP total without a second round trip.
export async function completeLesson(lessonId: string): Promise<CompleteLessonResponse> {
  const { data } = await apiClient.post<CompleteLessonResponse>('/api/v1/progress/complete', { lesson_id: lessonId });
  return data;
}

export interface ProgressSummary {
  progress: Array<{ lesson_id: string; slide_index: number; status: ProgressStatus; xp: number; updated_at: string }>;
  totals: { lessons_touched: number; lessons_completed: number; xp: number };
}

// Every lesson's progress + the running XP total across the whole course
// (GET /api/v1/progress/summary). Used to show a real, working "N XP" badge
// instead of a client-only counter that never touched the server.
export async function getProgressSummary(): Promise<ProgressSummary> {
  const { data } = await apiClient.get<ProgressSummary>('/api/v1/progress/summary');
  return data;
}
